import React from "react";
import { Form, Formik } from "formik";
import { Season, SeasonFormDefault } from "api/model/Season";
import { useOnSubmitFormik } from "api/hooks/useOnSubmitFormik";
import { Collections } from "api/model/Collections";
import { Box, Button, Grid } from "@material-ui/core";
import { InputFieldFormik } from "ui/componentes/Form/InputFieldFormik";
import { DateFieldFormik } from "ui/componentes/Form/DateFieldFormik";
import {
    BoxCard,
    SubmitButton,
    SwitchFieldFormik,
    yupMaxLengthField,
    yupMinLengthField,
    yupRequiredField,
} from "ui/componentes";
import * as Yup from "yup";
import { useCurrentSeason } from "../../../api";
import { useSnackbar } from "notistack";

interface SeasonFormProps {
    season?: Season;
    onSave?: (seasonId: string) => void;
    onCancel?: (event: {}, reason: "backdropClick" | "escapeKeyDown") => void;
}

const onClickCancel = (onCancel) => () => {
    if (onCancel) {
        onCancel();
    }
};

export const SeasonValidation = Yup.object().shape({
    name: Yup.string().required(yupRequiredField).max(50, yupMaxLengthField),
    startAt: Yup.string().required(yupRequiredField),
    endAt: Yup.string().required(yupRequiredField),
    current: Yup.boolean().required(yupRequiredField),
    year: Yup.string()
        .required(yupRequiredField)
        .min(4, yupMinLengthField)
        .max(4, yupMaxLengthField),
});

const SeasonForm: React.FC<SeasonFormProps> = ({
    season,
    onSave,
    onCancel,
}) => {
    const currentSeason = useCurrentSeason();
    const { enqueueSnackbar } = useSnackbar();
    const onSubmit = useOnSubmitFormik<Season>(Collections.SEASONS, {
        successMessage: season?.id ? "Temporada atualizada" : "Temporada salva",
        callback: onSave,
        beforeSubmit: (value) => {
            try {
                if (
                    value.id !== currentSeason?.id &&
                    value.current &&
                    currentSeason?.current
                ) {
                    enqueueSnackbar(
                        `Só pode existir uma temporada atual! ${currentSeason?.name} / ${currentSeason?.year} é a atual no momento.`,
                        { variant: "error", autoHideDuration: 10000 }
                    );
                    new Error("Already exists a current season");
                }
            } catch (e) {
                console.error(e);
                throw e;
            }
        },
    });

    return (
        <Formik
            enableReinitialize={true}
            initialValues={season || SeasonFormDefault}
            onSubmit={onSubmit}
            validationSchema={SeasonValidation}
        >
            {() => {
                return (
                    <Form>
                        <BoxCard>
                            <Grid container spacing={1}>
                                <Grid item xs={12}>
                                    <InputFieldFormik
                                        id="year"
                                        name="year"
                                        label="Ano"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <InputFieldFormik
                                        id="name"
                                        name="name"
                                        label="Nome"
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <DateFieldFormik
                                        id="startAt"
                                        name="startAt"
                                        label="Inicia em"
                                        fieldType="keyboardDatePicker"
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <DateFieldFormik
                                        id="endAt"
                                        name="endAt"
                                        label="Inicia em"
                                        fieldType="keyboardDatePicker"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <SwitchFieldFormik
                                        name="current"
                                        label="Temporada atual?"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Box
                                        display="flex"
                                        flexDirection="row-reverse"
                                        justifyContent=""
                                    >
                                        <SubmitButton
                                            style={{ marginLeft: 5 }}
                                            fullWidth={false}
                                        />
                                        <Button
                                            fullWidth={false}
                                            onClick={onClickCancel(onCancel)}
                                        >
                                            Cancelar
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </BoxCard>
                    </Form>
                );
            }}
        </Formik>
    );
};

export { SeasonForm };
